import { useState } from 'react'
import { CreditCardOutlined } from '@ant-design/icons'
import { Spin, List, message } from 'antd'
import { updatePaymentOptionsInApolloCache } from '@User/MyAccountPage/PaymentMethods/logic'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import Image from '~/core/Image'
import { IdentityType } from '~/redux-modules/identity/type'
import icons from '~/apps/consumer/icons'
import { NewPaymentModal } from '~/core/NewPaymentModal'
import { DeleteWarningModal } from '../components'
import pageStyles from '../styles.css'
import {
    BORDER,
    TEXT_H3_STYLE,
    ICON_STYLE,
    TEXT_LABEL_STYLE,
} from '../styleConstants'
import {
    useDeletePaymentOptionMutation,
    useUserPaymentOptionsUserProfileQuery,
} from '~/gql/graphql'
import logger from '~/lib/logger'

import { PaymentOption } from './components'
import {
    REMOVE_CARD_WARNING_TITLE,
    REMOVE_CARD_WARNING_BUTTON_TEXT,
} from './constants'
import styles from './styles.css'

type PaymentMethodsPropsType = {
    identity: IdentityType
    selectedField: string | null
    handleSelectEdit: (selectedField: string | null) => void
    popConfirmation?: string | null
    setCurrentForm: Function
}

function StripeBanner() {
    return (
        <Box
            className={pageStyles.mobileColBox}
            borderBottom={BORDER}
            paddingBottom="24px"
        >
            <Box className={`${pageStyles.mobileColBox} ${styles.stripeBox}`}>
                <Image src={icons.stripeIcon} height="28px" width="129.88px" />
            </Box>
            <Text color="blackAlpha45" className={styles.stripeText}>
                To ensure your data is safe, we use Stripe to process payments
                and store your information. Your data is encrypted using HTTPS.
                Fictiv does not directly process or store your credit card
                information at any time.
            </Text>
        </Box>
    )
}

export default function PaymentMethods(props: PaymentMethodsPropsType) {
    const { identity } = props
    const [removeCard, setRemoveCard] = useState<string | null>(null)
    const [newPaymentFormModal, setNewPaymentFormModal] =
        useState<boolean>(false)
    const { data, loading, refetch } = useUserPaymentOptionsUserProfileQuery()
    const [deletePaymentOption, { loading: removeLoading }] =
        useDeletePaymentOptionMutation({
            update: (cache, { data: mutationResult }) => {
                updatePaymentOptionsInApolloCache(
                    cache,
                    identity.id,
                    mutationResult?.user?.removeCreditCard || [],
                )
            },
            onError: (err, options) => {
                message.error('Failed to delete payment')

                logger.exception(err.message, {
                    extra: {
                        info: 'MyAccountPage::Failed to delete payment option',
                        identityId: identity.id,
                        cardId: options?.variables?.cardId,
                    },
                })
            },
        })

    const paymentOptions = data?.me?.paymentOptions || null

    return (
        <Box className={pageStyles.section}>
            {newPaymentFormModal && (
                <NewPaymentModal
                    identity={identity}
                    visible={newPaymentFormModal}
                    onClose={() => setNewPaymentFormModal(false)}
                    onPaymentAdded={() => refetch()}
                    type="card"
                />
            )}
            {removeCard && (
                <DeleteWarningModal
                    visible={Boolean(removeCard)}
                    title={REMOVE_CARD_WARNING_TITLE}
                    text={REMOVE_CARD_WARNING_BUTTON_TEXT}
                    onClose={() => setRemoveCard(null)}
                    loading={removeLoading}
                    onConfirm={async () => {
                        await deletePaymentOption({
                            variables: {
                                id: identity.id,
                                cardId: removeCard,
                            },
                        })

                        setRemoveCard(null)
                    }}
                />
            )}
            <Box className={pageStyles.header}>
                <CreditCardOutlined style={ICON_STYLE} />
                <Text style={TEXT_H3_STYLE}>Payment methods</Text>
            </Box>
            <Box padding="24px 0" className={pageStyles.mobileColBox}>
                <Text style={TEXT_LABEL_STYLE}>SAVED METHODS</Text>
                {loading && <Spin />}
                {!loading && !paymentOptions?.length && (
                    <Text>No payment methods saved</Text>
                )}
                {Boolean(paymentOptions?.length) && (
                    <Box width="100%">
                        <List
                            itemLayout="horizontal"
                            style={{ width: '100%' }}
                            dataSource={paymentOptions || []}
                            renderItem={payment => (
                                <PaymentOption
                                    payment={payment}
                                    setRemoveCard={setRemoveCard}
                                    selectedField={props.selectedField}
                                />
                            )}
                        />
                    </Box>
                )}
            </Box>
            <StripeBanner />
            <Box padding="24px 0">
                <Button
                    className={pageStyles.mobileLargeWidth}
                    name="add-new-card"
                    placement="user-profile-payment-methods"
                    data-test-target="user-profile-add-new-card"
                    onClick={() => setNewPaymentFormModal(true)}
                >
                    Add new card
                </Button>
            </Box>
        </Box>
    )
}
